<script setup>
import { ref, watch, computed } from 'vue';
import { Check, Crown, Flame, ShieldCheck, X } from 'lucide-vue-next';
import { usePage } from '@inertiajs/vue3';
import { trackEvent } from '@/Helpers/analytics';
import { trackAuditionAnalyticsEvent } from '@/Helpers/auditions';

const STAR_ICON_URL = 'https://sm.naughtycdn.com/images/landers/prize/NA-Star.png';

const emit = defineEmits(['close']);

const props = defineProps({
    isOpen: {
        type: Boolean,
        required: true,
    },
    audition: {
        type: Object,
        default: null,
    },
    // URL to redirect logged-in users to complete age verification
    ageVerifyUrl: {
        type: String,
        default: '',
    },
});

const isVisible = ref(false);
const source = 'SFW_Join_Modal'

watch(
    () => props.isOpen,
    (open) => {
        isVisible.value = open;
    },
    { immediate: true },
);

const page = usePage();
const isLoggedIn = computed(() => !!(page.props.auth?.user?.id));
const isAuditionsContext = computed(() => !!props.audition);

const progressPercentage = computed(() => {
    if (!props.audition) return 0;
    const result = (props.audition.likes / props.audition.star_goal) * 100;
    return Math.min(Math.round(result), 100);
});

const starsNeeded = computed(() => {
    if (!props.audition) return 0;
    return props.audition.star_goal - props.audition.likes;
});

const joinOptionsData = page.props.joinOptions;

const trialOption = computed(() => {
    if (!joinOptionsData?.button_trial) return null;
    const trialUrl =
        joinOptionsData.button_trial.url +
        (joinOptionsData.button_trial.url.includes('?') ? '&' : '?') +
        'vendor_option_id=2007234';
    return {
        name: joinOptionsData.button_trial.data.name,
        stars: joinOptionsData.button_trial.data.stars,
        stars_text: joinOptionsData.button_trial.data.stars_text,
        url: trialUrl,
        type: 'trial',
    };
});

const currentUrl = computed(() => {
    if (typeof window !== 'undefined' && window.location) {
        return window.location.pathname + window.location.search;
    }
    return '';
});

const email = ref('');

// Logged-in user: redirect to age verification page
const handleVerifyAge = () => {
    if (!props.ageVerifyUrl) return;

    trackEvent('Age Verify CTA Clicked', { source: source });
    window.location.href = props.ageVerifyUrl;
};

const emailError = ref('');

// Guest user: redirect to join/trial
const handleJoin = () => {
    if (!trialOption.value) return;

    const trimmedEmail = email.value.trim();
    emailError.value = '';

    let joinUrl =
        trialOption.value.url +
        (trialOption.value.url.includes('?')
            ? `&custom8=${source}`
            : `?custom8=${source}`);

    if (trimmedEmail) {
        joinUrl += `&email=${encodeURIComponent(trimmedEmail)}`;
    }

    console.log('++ SfwJoinModal: From ' + source + ' Redirecting to', joinUrl);

    localStorage.setItem('blue_join_options_source', source);
    localStorage.setItem('blue_join_options_join_type', trialOption.value.type);
    localStorage.setItem('blue_join_options_url', joinUrl);

    if (props.audition) {
        LTIAnalytics.setCookie('gold_upgrade_source', source);
        LTIAnalytics.setCookie('gold_upgrade_video_id', props.audition.id ?? '');
        trackAuditionAnalyticsEvent('Biller Join Button Clicked', props.audition, {
            store_in_local_storage: true,
            local_storage_key: 'blue_join_options_video_props',
            append_data: {
                source: source,
                join_type: trialOption.value.type,
            },
        });
    } else {
        trackEvent('Biller Join Button Clicked', {
            source: source,
            join_type: trialOption.value.type,
        });
    }

    window.location.href = joinUrl;
};
</script>

<template>
    <Teleport to="body">
        <Transition name="sfw-modal">
            <div
                v-if="isOpen"
                class="fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-6 overflow-y-auto"
            >
                <!-- Backdrop -->
                <div
                    class="absolute inset-0 bg-black/80 backdrop-blur-sm"
                    aria-hidden="true"
                />

                <!-- Modal Container -->
                <div
                    class="relative w-full max-w-[440px] my-auto transform overflow-hidden rounded-2xl sm:rounded-3xl bg-zinc-950 border border-zinc-800 shadow-2xl transition-all duration-300 ease-out max-h-[90vh]"
                    :class="
                        isVisible
                            ? 'scale-100 opacity-100 translate-y-0'
                            : 'scale-95 opacity-0 translate-y-4'
                    "
                    role="dialog"
                    aria-modal="true"
                    aria-labelledby="sfw-modal-title"
                >
                    <!-- Close Button -->
                    <button
                        @click="emit('close')"
                        class="absolute top-4 right-4 z-10 flex items-center justify-center w-8 h-8 rounded-full bg-zinc-800 hover:bg-zinc-700 text-zinc-400 hover:text-white transition-colors focus:outline-none focus:ring-2 focus:ring-zinc-600"
                        aria-label="Close"
                    >
                        <X class="w-4 h-4" />
                    </button>

                    <div class="p-4 sm:p-6 md:p-8">
                        <!-- ============================================ -->
                        <!-- SCENARIO A: Logged-in user, needs age verify -->
                        <!-- ============================================ -->
                        <template v-if="isLoggedIn">
                            <!-- Header Icon -->
                            <div class="flex justify-center mb-4 sm:mb-6">
                                <div class="relative">
                                    <div class="absolute inset-0 bg-amber-500/20 blur-xl rounded-full" />
                                    <div
                                        class="relative flex items-center justify-center w-16 h-16 sm:w-20 sm:h-20 bg-gradient-to-b from-zinc-800 to-zinc-950 rounded-full border border-zinc-700 shadow-inner"
                                    >
                                        <ShieldCheck class="w-8 h-8 sm:w-10 sm:h-10 text-amber-400" />
                                    </div>
                                    <div
                                        class="absolute -top-1 left-1/2 -translate-x-1/2 bg-amber-500 text-zinc-950 text-[10px] font-bold px-2 py-0.5 rounded-full border border-zinc-900 whitespace-nowrap"
                                    >
                                        REQUIRED
                                    </div>
                                </div>
                            </div>

                            <!-- Text -->
                            <div class="text-center mb-4 sm:mb-6">
                                <h2
                                    id="sfw-modal-title"
                                    class="text-xl sm:text-2xl font-bold text-white mb-2 sm:mb-3 tracking-tight"
                                >
                                    Age Verification Required
                                </h2>
                                <p class="text-zinc-400 text-xs sm:text-sm leading-relaxed">
                                    Your location requires a quick one-time age verification before
                                    you can access content. It only takes a moment.
                                </p>
                            </div>

                            <!-- Audition context card (if present) -->
                            <div
                                v-if="isAuditionsContext"
                                class="bg-zinc-900/50 rounded-xl p-3 sm:p-4 mb-4 sm:mb-6 border border-zinc-800/50 flex items-center gap-3"
                            >
                                <div class="relative w-12 h-12 rounded-lg overflow-hidden ring-2 ring-zinc-700 shrink-0">
                                    <img
                                        :src="audition.creator?.avatar"
                                        :alt="audition.creator?.name"
                                        class="w-full h-full object-cover"
                                    />
                                </div>
                                <div class="min-w-0">
                                    <p class="text-sm font-semibold text-white">
                                        {{ audition.creator?.name }}
                                    </p>
                                    <p class="text-xs text-zinc-400 line-clamp-1">
                                        Verify to continue watching
                                    </p>
                                </div>
                            </div>

                            <!-- What to expect -->
                            <div class="bg-zinc-900/50 rounded-xl p-3 sm:p-4 mb-4 sm:mb-6 border border-zinc-800/50 space-y-2">
                                <div class="flex items-start gap-2">
                                    <Check class="w-4 h-4 text-emerald-400 flex-shrink-0 mt-0.5" />
                                    <span class="text-xs sm:text-sm text-zinc-300">Quick one-time process</span>
                                </div>
                                <div class="flex items-start gap-2">
                                    <Check class="w-4 h-4 text-emerald-400 flex-shrink-0 mt-0.5" />
                                    <span class="text-xs sm:text-sm text-zinc-300">Your data stays private</span>
                                </div>
                                <div class="flex items-start gap-2">
                                    <Check class="w-4 h-4 text-emerald-400 flex-shrink-0 mt-0.5" />
                                    <span class="text-xs sm:text-sm text-zinc-300">Required by law in your region</span>
                                </div>
                            </div>

                            <!-- CTA -->
                            <button
                                @click="handleVerifyAge"
                                class="group relative overflow-hidden w-full inline-flex flex-col items-center justify-center rounded-xl py-3 sm:py-4 px-4 sm:px-6 font-medium transition-all focus:outline-none focus:ring-2 focus:ring-amber-500 focus:ring-offset-2 focus:ring-offset-zinc-950 active:scale-[0.98] bg-amber-500 hover:bg-amber-400 text-zinc-950 shadow-lg shadow-amber-500/25"
                            >
                                <div class="relative z-10 flex items-center justify-center gap-2 text-sm sm:text-base font-bold">
                                    <ShieldCheck class="w-4 h-4 sm:w-5 sm:h-5" />
                                    Verify My Age
                                </div>
                            </button>
                        </template>

                        <!-- ============================================ -->
                        <!-- SCENARIO B: Guest user, needs to join        -->
                        <!-- ============================================ -->
                        <template v-else>
                            <!-- Header Icon -->
                            <div class="flex justify-center mb-4 sm:mb-6">
                                <div class="relative">
                                    <div class="absolute inset-0 bg-blue-500/20 blur-xl rounded-full" />
                                    <div
                                        class="relative flex items-center justify-center w-16 h-16 sm:w-20 sm:h-20 bg-gradient-to-b from-zinc-800 to-zinc-950 rounded-full border border-zinc-700 shadow-inner"
                                    >
                                        <img
                                            :src="STAR_ICON_URL"
                                            alt="Naughty America"
                                            class="w-8 h-8 sm:w-10 sm:h-10"
                                        />
                                    </div>
                                    <div
                                        class="absolute -top-1 left-1/2 -translate-x-1/2 bg-amber-500 text-zinc-950 text-[10px] font-bold px-2 py-0.5 rounded-full border border-zinc-900 whitespace-nowrap"
                                    >
                                        FREE
                                    </div>
                                </div>
                            </div>

                            <!-- Text -->
                            <div class="text-center mb-4 sm:mb-6">
                                <h2
                                    id="sfw-modal-title"
                                    class="text-xl sm:text-2xl font-bold text-white mb-2 sm:mb-3 tracking-tight leading-tight"
                                >
                                    <template v-if="isAuditionsContext">
                                        Help
                                        <span class="text-blue-400">{{ audition.creator?.name }}</span>
                                        Reach Her Goal
                                    </template>
                                    <template v-else>Activate FREE Stars</template>
                                </h2>
                                <p class="text-zinc-400 text-xs sm:text-sm leading-relaxed">
                                    <template v-if="isAuditionsContext">
                                        Your membership gives you FREE stars to sponsor this plus limited access to Naughty America HD, 4K & VR videos.
                                    </template>
                                    <template v-else>
                                        Your sponsorship turns Auditions into Productions. Support your favorite performers and help decide what content gets made.
                                    </template>
                                </p>
                            </div>

                            <!-- Audition Context: Progress & Stats -->
                            <div
                                v-if="isAuditionsContext"
                                class="bg-zinc-900/50 rounded-xl p-3 sm:p-4 md:p-5 mb-4 sm:mb-6 border border-zinc-800/50"
                            >
                                <!-- Performer Info -->
                                <div class="flex items-center gap-3 mb-3 sm:mb-4">
                                    <div
                                        class="relative w-12 h-12 rounded-lg overflow-hidden ring-2 ring-zinc-700 shrink-0"
                                    >
                                        <img
                                            :src="audition.creator?.avatar"
                                            :alt="audition.creator?.name"
                                            class="w-full h-full object-cover"
                                        />
                                    </div>
                                    <div class="min-w-0">
                                        <p class="text-sm font-semibold text-white">
                                            {{ audition.creator?.name }}
                                        </p>
                                        <p class="text-xs text-zinc-400 line-clamp-1">
                                            {{ audition.title }}
                                        </p>
                                    </div>
                                </div>

                                <!-- Progress Bar -->
                                <div class="mb-3 sm:mb-4">
                                    <div class="flex justify-between items-end mb-1.5 sm:mb-2">
                                        <div class="text-xs sm:text-sm font-medium text-white flex items-center gap-1.5">
                                            <img :src="STAR_ICON_URL" alt="star" class="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                                            <span>{{ audition.likes }}</span>
                                            <span class="text-zinc-500 font-normal text-[11px] sm:text-xs"
                                                >/ {{ audition.star_goal }} funded</span
                                            >
                                        </div>
                                        <div class="flex items-center gap-1.5 sm:gap-2">
                                            <span
                                                v-if="starsNeeded > 0"
                                                class="text-[10px] sm:text-xs font-semibold text-emerald-400"
                                            >
                                                {{ starsNeeded }} needed
                                            </span>
                                            <span class="text-[10px] sm:text-xs font-medium text-blue-400"
                                                >{{ progressPercentage }}%</span
                                            >
                                        </div>
                                    </div>
                                    <div class="h-2 w-full bg-zinc-800 rounded-full overflow-hidden">
                                        <div
                                            class="h-full bg-gradient-to-r from-blue-600 to-blue-400 rounded-full transition-all duration-1000 ease-out"
                                            :style="{ width: `${progressPercentage}%` }"
                                        />
                                    </div>
                                </div>

                                <!-- Stats -->
                                <div class="space-y-2">
                                    <div
                                        v-if="audition.today_sponsors_count > 0"
                                        class="flex items-center gap-2 sm:gap-3 text-xs sm:text-sm text-zinc-300"
                                    >
                                        <div
                                            class="w-6 h-6 rounded-full bg-amber-500/10 flex items-center justify-center shrink-0"
                                        >
                                            <Crown class="w-3.5 h-3.5 text-amber-500" />
                                        </div>
                                        <span>
                                            <strong class="text-white">{{
                                                audition.today_sponsors_count
                                            }}</strong>
                                            sponsors today
                                        </span>
                                    </div>
                                    <div v-else class="flex items-center gap-2 sm:gap-3 text-xs sm:text-sm text-zinc-300">
                                        <div
                                            class="w-6 h-6 rounded-full bg-amber-500/10 flex items-center justify-center shrink-0"
                                        >
                                            <Crown class="w-3.5 h-3.5 text-amber-500" />
                                        </div>
                                        <span class="text-white"
                                            >No sponsors today,
                                            <strong>be the first!</strong></span
                                        >
                                    </div>
                                    <div
                                        v-if="audition.top_sponsor_stars > 0"
                                        class="flex items-center gap-2 sm:gap-3 text-xs sm:text-sm text-zinc-300"
                                    >
                                        <div
                                            class="w-6 h-6 rounded-full bg-orange-500/10 flex items-center justify-center shrink-0"
                                        >
                                            <Flame class="w-3.5 h-3.5 text-orange-500" />
                                        </div>
                                        <span>
                                            Top sponsor gave
                                            <strong class="text-white"
                                                >{{ audition.top_sponsor_stars }} Stars</strong
                                            >
                                        </span>
                                    </div>
                                </div>
                            </div>

                            <!-- Email Input -->
                            <div class="mb-3 sm:mb-4">
                                <input
                                    v-model="email"
                                    type="email"
                                    placeholder="Enter your email (optional)"
                                    :class="[
                                        'w-full h-10 sm:h-11 rounded-xl bg-zinc-900 border text-white text-xs sm:text-sm placeholder-zinc-500 px-3 sm:px-4 focus:outline-none focus:ring-1 transition-colors',
                                        emailError
                                            ? 'border-red-500 focus:border-red-500 focus:ring-red-500'
                                            : 'border-zinc-700 focus:border-blue-500 focus:ring-blue-500'
                                    ]"
                                    @input="emailError = ''"
                                />
                                <p v-if="emailError" class="mt-1.5 text-xs text-red-400">
                                    {{ emailError }}
                                </p>
                            </div>

                            <!-- CTA Button -->
                            <div class="space-y-2 sm:space-y-3">
                                <button
                                    @click="handleJoin"
                                    class="group relative overflow-hidden w-full inline-flex flex-col items-center justify-center rounded-xl py-3 sm:py-4 px-4 sm:px-6 font-medium transition-all focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 focus:ring-offset-zinc-950 active:scale-[0.98] bg-blue-600 hover:bg-blue-500 text-white shadow-lg shadow-blue-500/25 border border-blue-500"
                                >
                                    <div
                                        class="absolute inset-0 bg-gradient-to-r from-blue-600 to-blue-500 opacity-100 transition-opacity group-hover:opacity-90"
                                    />
                                    <div
                                        class="relative z-10 flex items-center justify-center gap-1.5 sm:gap-2 text-base sm:text-lg font-bold"
                                    >
                                        <span class="whitespace-nowrap">Give 5 FREE Stars —</span>
                                        <span class="whitespace-nowrap">$0.00*</span>
                                    </div>
                                </button>

                                <p class="text-center text-zinc-500 text-[10px] sm:text-xs mt-0.5">
                                    *Get 3 days free, cancel anytime.
                                </p>

                                <p class="text-center text-xs sm:text-sm text-zinc-500">
                                    Already a member?
                                    <a
                                        :href="route('members2.auth.login', { dest: currentUrl })"
                                        class="text-blue-400 hover:text-blue-300 font-semibold transition-colors"
                                    >
                                        Sign in
                                    </a>
                                </p>
                            </div>
                        </template>
                    </div>
                </div>
            </div>
        </Transition>
    </Teleport>
</template>

<style scoped>
.sfw-modal-enter-active,
.sfw-modal-leave-active {
    transition: opacity 0.3s ease;
}

.sfw-modal-enter-from,
.sfw-modal-leave-to {
    opacity: 0;
}
</style>
